import { useEffect } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminOverviewTab from './components/AdminOverviewTab';
import AdminShipmentsTab from './components/AdminShipmentsTab';
import AdminCreateTab from './components/AdminCreateTab';
import AdminSiteSettingsTab from './components/AdminSiteSettingsTab';
import AdminManageAdminsTab from './components/AdminManageAdminsTab';
import AdminEmailTestTab from './components/AdminEmailTestTab';
import AdminTabErrorBoundary from '../../components/base/AdminTabErrorBoundary';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { useAdminAuth } from '../../hooks/useAdminAuth';

type AdminTab = 'overview' | 'shipments' | 'create' | 'admins' | 'settings' | 'email-test';

const BASE_TABS: { id: AdminTab; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview', icon: 'ri-dashboard-3-line' },
  { id: 'shipments', label: 'Shipments', icon: 'ri-box-3-line' },
  { id: 'create', label: 'Create Shipment', icon: 'ri-add-circle-line' },
  { id: 'email-test', label: 'Email Test', icon: 'ri-mail-settings-line' },
  { id: 'settings', label: 'Site Settings', icon: 'ri-settings-3-line' },
];

const SUPER_TABS: { id: AdminTab; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview', icon: 'ri-dashboard-3-line' },
  { id: 'shipments', label: 'Shipments', icon: 'ri-box-3-line' },
  { id: 'create', label: 'Create Shipment', icon: 'ri-add-circle-line' },
  { id: 'admins', label: 'Manage Admins', icon: 'ri-user-settings-line' },
  { id: 'email-test', label: 'Email Test', icon: 'ri-mail-settings-line' },
  { id: 'settings', label: 'Site Settings', icon: 'ri-settings-3-line' },
];

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const { settings } = useSiteSettings();
  const { isAdmin, isSuperAdmin, loading, signOut, adminRecord } = useAdminAuth();
  const navigate = useNavigate();
  const now = new Date();

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/admin/login', { replace: true });
    }
  }, [isAdmin, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#071818' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-teal-600 text-sm">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const TABS = isSuperAdmin ? SUPER_TABS : BASE_TABS;

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ background: '#071818', color: 'white' }}>
      {/* Admin Navbar */}
      <header className="sticky top-0 z-40 border-b border-teal-900/40" style={{ background: '#0d2020' }}>
        <div className="flex items-center justify-between px-6" style={{ height: '64px' }}>
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--primary)' }}>
              <i className="ri-flight-takeoff-line text-white text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-white text-base">{settings.companyName}</span>
              <span className="px-2 py-0.5 rounded text-xs font-bold text-red-300 border border-red-500/40" style={{ background: 'rgba(239,68,68,0.1)' }}>
                ADMIN
              </span>
            </div>
          </div>

          {/* Center: tab nav */}
          <nav className="hidden md:flex items-center gap-1">
            {TABS.map(({ id, label, icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === id ? 'text-white' : 'text-teal-500 hover:text-teal-200 hover:bg-teal-900/20'
                }`}
                style={activeTab === id ? { background: 'var(--primary)' } : {}}
              >
                <i className={icon} />
                {label}
              </button>
            ))}
          </nav>

          {/* Right */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-teal-400 text-xs hidden md:block">System Online</span>
            </div>
            {adminRecord && (
              <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-900/20 border border-teal-900/40">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white uppercase" style={{ background: 'var(--primary)' }}>
                  {adminRecord.email.charAt(0)}
                </div>
                <span className="text-teal-300 text-xs font-medium max-w-[120px] truncate">{adminRecord.email}</span>
                {isSuperAdmin && <span className="text-amber-400 text-xs"><i className="ri-star-fill" /></span>}
              </div>
            )}
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-900/20 text-sm transition-colors cursor-pointer whitespace-nowrap border border-red-900/30"
            >
              <i className="ri-logout-box-r-line text-sm" />
              <span className="hidden md:block">Sign Out</span>
            </button>
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-teal-400 hover:text-white hover:bg-teal-900/30 text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line text-sm" />
              <span className="hidden md:block">View Site</span>
            </Link>
          </div>
        </div>

        {/* Mobile tab bar */}
        <div className="md:hidden flex overflow-x-auto gap-1 px-4 pb-3">
          {TABS.map(({ id, label, icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === id ? 'text-white' : 'text-teal-500'
              }`}
              style={activeTab === id ? { background: 'var(--primary)' } : {}}
            >
              <i className={icon} />
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* Breadcrumb bar */}
      <div className="border-b border-teal-900/20 px-6 py-3" style={{ background: '#0d2020' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-teal-600 text-xs">
            <span>Admin</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-teal-400">{TABS.find((t) => t.id === activeTab)?.label}</span>
          </div>
          <span className="text-teal-700 text-xs">
            {now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>
      </div>

      {/* Page content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'color-mix(in srgb, var(--primary) 20%, transparent)' }}>
              <i className={`${TABS.find((t) => t.id === activeTab)?.icon} text-lg`} style={{ color: 'var(--primary)' }} />
            </div>
            <h1 className="font-heading font-bold text-2xl text-white">
              {TABS.find((t) => t.id === activeTab)?.label}
            </h1>
          </div>
          {activeTab === 'overview'    && <p className="text-teal-600 text-sm ml-13">System-wide performance at a glance.</p>}
          {activeTab === 'shipments'   && <p className="text-teal-600 text-sm ml-13">Search, filter, and manage all shipments from the database.</p>}
          {activeTab === 'create'      && <p className="text-teal-600 text-sm ml-13">Add a new shipment — it saves directly to the database.</p>}
          {activeTab === 'admins'      && <p className="text-teal-600 text-sm ml-13">Invite team members and manage admin access.</p>}
          {activeTab === 'email-test'  && <p className="text-teal-600 text-sm ml-13">Send a test email to verify Resend delivery is working correctly.</p>}
          {activeTab === 'settings'    && <p className="text-teal-600 text-sm ml-13">Customize colors, contact info, and branding for the public website.</p>}
        </div>

        {activeTab === 'overview'   && <AdminTabErrorBoundary tabName="Overview"><AdminOverviewTab /></AdminTabErrorBoundary>}
        {activeTab === 'shipments'  && <AdminTabErrorBoundary tabName="Shipments"><AdminShipmentsTab /></AdminTabErrorBoundary>}
        {activeTab === 'create'     && <AdminTabErrorBoundary tabName="Create Shipment"><AdminCreateTab /></AdminTabErrorBoundary>}
        {activeTab === 'admins'     && <AdminTabErrorBoundary tabName="Manage Admins"><AdminManageAdminsTab /></AdminTabErrorBoundary>}
        {activeTab === 'email-test' && <AdminTabErrorBoundary tabName="Email Test"><AdminEmailTestTab /></AdminTabErrorBoundary>}
        {activeTab === 'settings'   && <AdminTabErrorBoundary tabName="Site Settings"><AdminSiteSettingsTab /></AdminTabErrorBoundary>}
      </main>
    </div>
  );
}
